<template>
  <div
    class="c-app"
    :class="{ 'module-appointmentpass': isActiveAppToLocations }"
  >
    <TheSidebar
      tabindex="-1"
      :aria-hidden="isDialogOpen ? 'true' : undefined"
      :inert="isDialogOpen ? '' : null"
    />
    <TheAside
      tabindex="-1"
      :aria-hidden="isDialogOpen ? 'true' : undefined"
      :inert="isDialogOpen ? '' : null"
    />
    <div class="wrapper">
      <div class="layout-content">
        <div
          :aria-hidden="isPluginOrActionModalOpen ? 'true' : undefined"
          :inert="isPluginOrActionModalOpen ? '' : null"
        >
          <TheHeader />
        </div>
        <div
          class="c-body"
          :aria-hidden="isDialogOpen ? 'true' : undefined"
          :inert="isDialogOpen ? '' : null"
        >
          <main
            class="c-main"
            role="main"
            :class="[
              viewClass,
              {
                'pb-6': devicePlatform === 'iOS',
                'queue-background':
                  isQueueActiveRoute || isAdvanceReportingActiveRoute
              }
            ]"
          >
            <CustomScrollbar
              ref="mainScrollbarRef"
              :swicher="state.scrollbarSettings.swicher"
              :settings="state.scrollbarSettings"
              :scroll-component-class="['main-scroll-container']"
              @ps-scroll-y="handleScroll"
            >
              <CContainer fluid class="ps-0 pe-0">
                <router-view v-slot="{ Component }">
                  <transition name="fade">
                    <component :is="Component" />
                  </transition>
                </router-view>
              </CContainer>
            </CustomScrollbar>
          </main>
          <TheFooter />
        </div>
      </div>
      <CustomModal
        v-if="modal"
        size="sm"
        v-bind="modal.options"
        @close="closeModal"
      >
      </CustomModal>
      <ActionDialog
        v-if="actionDialog"
        @close="closeActionDialog"
        @confirm="onDialogConfirm"
        v-bind="actionDialog.options ? actionDialog.options.props : {}"
        :is-visible="actionDialog.isVisible"
      ></ActionDialog>
    </div>
  </div>
</template>

<script>
import {
  reactive,
  inject,
  computed,
  watch,
  onMounted,
  ref,
  provide,
  onUnmounted,
  nextTick
} from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import TheSidebar from "@/v3components/Sidebar/TheSidebar.vue"
import TheHeader from "@/v3components/Header/TheHeader.vue"
import ActionDialog from "@/v3components/shared/Alerts/ActionDialog.vue"
import TheAside from "@/v3components/Sidebar/TheAside.vue"
import TheFooter from "@/v3components/TheFooter.vue"
import helpers from "@/helpers/index"
import { MAIN_PERFECT_SCROLLBAR_REFRESH_EVENT } from "@/helpers/requestMainPerfectScrollbarRefresh.js"
import queueConstants from "@/constants/queueConstants"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import {
  startNetworkLatencyMonitoring,
  stopNetworkLatencyMonitoring
} from "@/helpers/networkSlowConnection.js"
import networkConnectivityConfig from "@/configs/networkConnectivityConfig.js"

const isNetworkBannerRoute = (routeName) =>
  routeName === "School Dashboard" || routeName === "Advanced Reporting"

export default {
  name: "SideBarLayout",
  components: {
    TheSidebar,
    TheHeader,
    ActionDialog,
    TheAside,
    TheFooter
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const modal = inject("modal")
    const actionDialog = inject("actionDialog")

    /** Shared with TheHeaderCreateButton so main/sidebar use inert + SR isolation while open. */
    const headerCreateModalOpen = ref(false)
    provide("headerCreateModalOpen", headerCreateModalOpen)

    const mainScrollbarRef = ref(null)

    const onMainPerfectScrollbarRefresh = () => {
      nextTick(() => {
        mainScrollbarRef.value?.updatePerfectScrollbar?.()
      })
    }

    const state = reactive({
      scrollbarSettings: {
        suppressScrollY: false,
        suppressScrollX: true,
        swicher: true
      },
      shouldFocusMainAfterSidebarEnter: false
    })

    const activeRoute = computed(() => {
      return store.getters["routerDependencies/activeRoute"]
    })

    const isQueueActiveRoute = computed(() => {
      return activeRoute?.value?.path === queueConstants.PATHS.SETTINGS
    })

    const isAdvanceReportingActiveRoute = computed(() => {
      return activeRoute?.value?.path === advanceReportingConstants.PATHS.REPORT
    })

    const syncNetworkMonitoringForRoute = () => {
      if (!networkConnectivityConfig.speedStatusEnabled) {
        stopNetworkLatencyMonitoring()
        return
      }
      if (isNetworkBannerRoute(route.name)) {
        store.dispatch("networkConnectivity/syncBrowserOnlineFromNavigator")
        startNetworkLatencyMonitoring()
      } else {
        stopNetworkLatencyMonitoring()
      }
    }

    const isActiveAppoinmentModule = computed(
      () => store.getters["schools/isActiveAppoinmentModule"]
    )

    const activeAppoinmentModuleOptions = computed(
      () => store.getters["schools/activeAppoinmentModuleOptions"]
    )

    const viewClass = computed(() => {
      return route.meta.classes ? route.meta.classes : ""
    })

    const isActiveAppToLocations = computed(() => {
      return (
        isActiveAppoinmentModule.value &&
        activeAppoinmentModuleOptions.value &&
        activeAppoinmentModuleOptions.value.location
      )
    })

    const isDialogOpen = computed(() => {
      return Boolean(
        modal?.options?.visible ||
        actionDialog?.isVisible ||
        headerCreateModalOpen.value
      )
    })

    /** Inert the header only for plugin/ActionDialog modals — not header Create menu (modal lives under header). */
    const isPluginOrActionModalOpen = computed(() =>
      Boolean(modal?.options?.visible || actionDialog?.isVisible)
    )

    const devicePlatform = computed(() => {
      const ua = navigator.userAgent
      if (/android/i.test(ua)) {
        return "Android"
      } else if (
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      ) {
        return "iOS"
      }
      return "Other"
    })

    const getMainFirstFocusable = () => {
      const mainRegion = document.querySelector("main.c-main")
      if (!mainRegion) {
        return null
      }
      const selectors = [
        "button:not([disabled])",
        "[href]",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ].join(",")
      const focusableElements = Array.from(
        mainRegion.querySelectorAll(selectors)
      )
      return (
        focusableElements.find((element) => {
          if (!element?.offsetParent) {
            return false
          }
          const style = window.getComputedStyle(element)
          return style.display !== "none" && style.visibility !== "hidden"
        }) || null
      )
    }

    const focusSidebarToggleButton = () => {
      const sidebarToggleButton = document.querySelector(
        ".v3-the-sidebar .v3-sidebar-toggler"
      )
      if (
        sidebarToggleButton &&
        typeof sidebarToggleButton.focus === "function"
      ) {
        sidebarToggleButton.focus()
      }
    }

    const onSidebarKeyboardRouteIntent = () => {
      state.shouldFocusMainAfterSidebarEnter = true
    }

    watch(
      () => route.path,
      async () => {
        syncNetworkMonitoringForRoute()
        helpers.scrollToTop()
        if (!state.shouldFocusMainAfterSidebarEnter) {
          return
        }
        await nextTick()
        const firstFocusable = getMainFirstFocusable()
        firstFocusable?.focus?.()
        state.shouldFocusMainAfterSidebarEnter = false
      }
    )

    onMounted(() => {
      syncNetworkMonitoringForRoute()
      const deviceWidth =
        window.innerWidth > 0 ? window.innerWidth : screen.width
      state.scrollbarSettings.swicher = !deviceWidth || deviceWidth > 700
      window.addEventListener(
        "sidebar-keyboard-route-intent",
        onSidebarKeyboardRouteIntent
      )
      window.addEventListener(
        MAIN_PERFECT_SCROLLBAR_REFRESH_EVENT,
        onMainPerfectScrollbarRefresh
      )
      nextTick(() => {
        focusSidebarToggleButton()
      })
    })

    onUnmounted(() => {
      stopNetworkLatencyMonitoring()
      window.removeEventListener(
        "sidebar-keyboard-route-intent",
        onSidebarKeyboardRouteIntent
      )
      window.removeEventListener(
        MAIN_PERFECT_SCROLLBAR_REFRESH_EVENT,
        onMainPerfectScrollbarRefresh
      )
    })

    const handleScroll = () => {
      const el = document.getElementById("scroller")
      if (el && el.__vue__) {
        el.__vue__.updateVisibleItems()
      }
    }

    const closeModal = () => {
      modal.close()
    }

    const closeActionDialog = () => {
      actionDialog.close()
    }
    const onDialogConfirm = () => {
      actionDialog.confirm()
    }

    return {
      isQueueActiveRoute,
      isAdvanceReportingActiveRoute,
      modal,
      actionDialog,
      state,
      viewClass,
      isActiveAppToLocations,
      isDialogOpen,
      isPluginOrActionModalOpen,
      devicePlatform,
      handleScroll,
      closeModal,
      closeActionDialog,
      onDialogConfirm,
      mainScrollbarRef
    }
  }
}
</script>

<style scoped>
.scrollTopFix {
  overflow: auto !important;
  width: calc(100% + 11px) !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
